import { useMemo } from 'react';
import { sortTags, TagsAndUsersSortValues, tagsColumns } from './utils';
import { useSelector } from 'react-redux';
import {
  selectCountSummaryByCrmOrgId,
  selectTagsList,
} from '../../../reducers/documentation/userInputsReducer';
import { colors, Tag as SweepTag } from '@sweep-io/sweep-design';
import { useInitializeUserInputs } from '../dependencies/hooks/useInitializeUserInputs';
import { TagsCenterLayout } from './TagsCenterLayout';
import { selectDependenciesHistory } from '../../../reducers/documentation/documentationReducer';
import useQueryParams from '../../../navigation-utils/useQueryParams';
import { useTags } from '../dependencies/annotations/tags/useTags';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { useRunOnceWhenTruthy } from '../../common/useRunOnceWhenTruthy';
import { Tag } from '../../../apis/facades/useUserInputsApiFacade';
import { extractShortUUID } from '../../../navigation-utils/readableUUID';
import { TagRow } from './types';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import sum from 'lodash/sum';
import { SortOrder } from '../../common/types';
import {
  selectTopBarSearchText,
  selectTopBarSort,
} from '../../../reducers/documentation/documentationTopBarReducer';
import { SubPageTypes } from '../types';

interface TagsCenterProps {
  crmOrgId: string;
}

const useHandleDeepLinkParams = ({
  crmOrgId,
  isLoading,
  tags,
}: {
  crmOrgId: string;
  isLoading: boolean;
  tags: Tag[];
}) => {
  const { onTagClick } = useTags(crmOrgId);
  const queryParams = useQueryParams();
  const configurationItemId = queryParams.get(DeepLinkSearchParams.configurationItemId);

  useRunOnceWhenTruthy(
    () => {
      const res = configurationItemId?.split('|') ?? [];
      const shortTagId = extractShortUUID(res[2]);
      const tag = tags.find((tag) => tag.id.startsWith(shortTagId ?? ''));

      if (tag) {
        onTagClick(null as any, { value: tag.id, label: tag.name, tagColor: tag.color }, true);
      }
    },
    Boolean(configurationItemId && !isLoading),
  );
};

export const TagsCenter = ({ crmOrgId }: TagsCenterProps) => {
  const dependenciesHistory = useSelector(selectDependenciesHistory);
  const countSummary = useSelector(selectCountSummaryByCrmOrgId(crmOrgId));
  const tags = useSelector(selectTagsList);
  const searchValue = useSelector(selectTopBarSearchText);
  const sortState = useSelector(selectTopBarSort);

  const isLoading = useInitializeUserInputs(crmOrgId);

  useHandleDeepLinkParams({
    crmOrgId,
    isLoading,
    tags,
  });

  const filteredTags = useMemo(() => {
    return tags.filter((tag) => tag.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [tags, searchValue]);

  const rows: TagRow[] = useMemo(() => {
    const _filteredTagsWithCounts = filteredTags.map((tag) => ({
      id: tag.id,
      _name: tag.name,
      name: (
        <SweepTag label={tag.name} color={(colors[tag.color as keyof typeof colors] as any)[100]} />
      ),
      color: (colors[tag.color as keyof typeof colors] as any)[100],
      isRowActive: dependenciesHistory?.[0]?.id === tag.id,
      count: sum(Object.values(countSummary?.[tag.id] ?? {}).map((value) => value.length)) ?? 0,
      [DocumentationTabTypes.RECORD_TYPES]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.RECORD_TYPES]?.length ?? 0,
      [DocumentationTabTypes.SF_AUTOMATIONS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.SF_AUTOMATIONS]?.length ?? 0,
      [DocumentationTabTypes.APEX]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.APEX]?.length ?? 0,
      [DocumentationTabTypes.FIELDS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.FIELDS]?.length ?? 0,
      [DocumentationTabTypes.VALIDATION_RULES]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.VALIDATION_RULES]?.length ?? 0,
      [DocumentationTabTypes.SWEEP_AUTOMATIONS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.SWEEP_AUTOMATIONS]?.length ?? 0,
      [DocumentationTabTypes.PLAYBOOK_ALERTS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.PLAYBOOK_ALERTS]?.length ?? 0,
      [DocumentationTabTypes.ASSIGNMENTS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.ASSIGNMENTS]?.length ?? 0,
      [DocumentationTabTypes.LAYOUTS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.LAYOUTS]?.length ?? 0,
      [DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]?.length ?? 0,
      [DocumentationTabTypes.MATCHING_DEDUPE]:
        countSummary?.[tag.id]?.[DocumentationTabTypes.MATCHING_DEDUPE]?.length ?? 0,
    }));

    return sortTags(
      _filteredTagsWithCounts,
      sortState ?? { sortBy: TagsAndUsersSortValues.name, sortOrder: SortOrder.ASC },
    );
  }, [filteredTags, dependenciesHistory, sortState, countSummary]);

  const sortedItems = useMemo(() => {
    if (!sortState) return rows;
    return rows.sort((a, b) => {
      const key = sortState.sortBy as keyof TagRow;
      const aValue = a[key];
      const bValue = b[key];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortState.sortOrder === SortOrder.ASC
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortState.sortOrder === SortOrder.DESC ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [rows, sortState]);

  return (
    <TagsCenterLayout
      sortProps={sortState ?? { sortBy: TagsAndUsersSortValues.name, sortOrder: SortOrder.ASC }}
      columns={tagsColumns}
      rows={sortedItems}
      crmOrgId={crmOrgId}
      label="Tags"
      pageType={SubPageTypes.tags}
    />
  );
};
